import React, { useEffect, useRef, useState } from 'react';
import { exchangeManagement, readManagement, revokeManagementShare, ManagementView } from '../../api/donorManagementApi';
import { features } from '../../config/features';
import '../VoiceInvitation/VoiceInvitation.css';

export default function DonorManagement({ enabled = features.accountlessDonorRoute }: {enabled?: boolean}) {
  const token = useRef(new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '');
  const started = useRef(false);
  const inFlight = useRef(false);
  const [view, setView] = useState<ManagementView | null>(null);
  const [state, setState] = useState<'preview'|'loading'|'active'|'revoked'|'unavailable'>(!enabled ? 'unavailable' : token.current ? 'preview' : 'loading');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    // Keep the fragment in memory only, before any network request. GET cannot exchange it.
    window.history.replaceState(null, '', window.location.pathname);
    if (!enabled || token.current) return;
    void readManagement().then(next => { setView(next); setState(next.status); }).catch(() => setState('unavailable'));
  }, [enabled]);
  const exchange = async () => {
    if (inFlight.current || !token.current) return;
    inFlight.current = true; setBusy(true); setError(false);
    try {
      const next = await exchangeManagement(token.current);
      setView(next); setState(next.status);
    } catch { setState('unavailable'); }
    finally { token.current = ''; setBusy(false); inFlight.current = false; }
  };
  const revoke = async () => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError(false);
    try { await revokeManagementShare(); setState('revoked'); }
    catch { setError(true); }
    finally { setBusy(false); inFlight.current = false; }
  };
  return <main className="donor-shell">
    <div className="donor-shell__brand" aria-label="Luma">Luma</div>
    <section className="donor-card" aria-busy={busy}>
      <h1>Manage voice sharing</h1>
      {state === 'preview' && <>
        <p>Continue to review the family sharing permission associated with this private link. The link expires after one hour and can be used once.</p>
        <button className="donor-button donor-button--primary" disabled={busy} onClick={() => void exchange()}>{busy ? 'Opening…' : 'Continue'}</button>
      </>}
      {state === 'loading' && <p role="status">Loading your sharing permission…</p>}
      {state === 'unavailable' && <p role="status">This management link is unavailable or has expired. Ask the person who invited you for a new management email.</p>}
      {state === 'active' && <>
        <p>Your voice is shared with {view?.listeners.length ? `the family of ${view.listeners.join(', ')}` : 'the family on this invitation'}.</p>
        <p>Stopping this share blocks future voice use for this family. Your voice model, recordings, consent history, and other family shares remain saved.</p>
        {confirm ? <div className="donor-confirm" role="group" aria-labelledby="management-confirm">
          <h2 id="management-confirm">Stop sharing with this family?</h2>
          <button className="donor-button donor-button--secondary" disabled={busy} onClick={() => setConfirm(false)}>Keep sharing</button>
          <button className="donor-button donor-button--danger" disabled={busy} onClick={() => void revoke()}>{busy ? 'Stopping…' : 'Yes, stop this share'}</button>
        </div> : <button className="donor-button donor-button--danger" onClick={() => setConfirm(true)}>Stop this family share</button>}
      </>}
      {state === 'revoked' && <p role="status">Sharing with this family has stopped. Your other sharing permissions are unchanged.</p>}
      {error && <p className="donor-error" role="alert">We couldn’t confirm the change. Try again, or request a fresh management email if this session has expired.</p>}
    </section>
    <p className="donor-shell__privacy">This private page manages one family share.</p>
  </main>;
}
